import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import MyTestsTab from "../components/tests/MyTestsTab";
import CreateEditTestWizard from "../components/tests/CreateEditTestWizard";
import ResultsGradingTab from "../components/tests/ResultsGradingTab";
import { useTranslation } from "../i18n/useTranslation";

const TAB_KEYS = {
  MY_TESTS: "myTests",
  CREATE_EDIT: "createEdit",
  RESULTS: "results",
};

export default function TeacherTestsPage({ isDarkMode }) {
  const { t } = useTranslation();

  // AIT-251: subtitles clarify what each tab manages.
  const TABS = [
    {
      key: TAB_KEYS.MY_TESTS,
      label: t("My Tests"),
      subtitle: t("Tests you create and assign to students"),
      showBadge: true,
    },
    { key: TAB_KEYS.CREATE_EDIT, label: t("Create / Edit Test"), showBadge: false },
    { key: TAB_KEYS.RESULTS, label: t("Results & Grading"), showBadge: false },
  ];
  const [activeTab, setActiveTab] = useState(TAB_KEYS.MY_TESTS);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [testCount, setTestCount] = useState(0);

  const colors = getColors(isDarkMode);

  const switchToTab = useCallback((tabKey) => {
    if (tabKey === TAB_KEYS.CREATE_EDIT) setSelectedTestId(null);
    setActiveTab(tabKey);
  }, []);

  const editTest = useCallback((testId) => {
    setSelectedTestId(testId);
    setActiveTab(TAB_KEYS.CREATE_EDIT);
  }, []);

  const viewResults = useCallback((testId) => {
    setSelectedTestId(testId);
    setActiveTab(TAB_KEYS.RESULTS);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_KEYS.MY_TESTS:
        return (
          <MyTestsTab
            isDarkMode={isDarkMode}
            onSwitchTab={switchToTab}
            onEditTest={editTest}
            onViewResults={viewResults}
          />
        );
      case TAB_KEYS.CREATE_EDIT:
        return (
          <CreateEditTestWizard
            isDarkMode={isDarkMode}
            editTestId={selectedTestId}
            onComplete={() => {
              setSelectedTestId(null);
              setActiveTab(TAB_KEYS.MY_TESTS);
            }}
          />
        );
      case TAB_KEYS.RESULTS:
        return (
          <ResultsGradingTab
            isDarkMode={isDarkMode}
            initialTestId={selectedTestId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.bg }]}
      contentContainerStyle={styles.content}
    >
      {/* Tab navigation */}
      <View style={[styles.tabNav, { backgroundColor: colors.cardBg, borderColor: colors.border }]}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tabBtn,
                isActive && styles.tabBtnActive,
                isActive && { backgroundColor: colors.accent },
                !isActive && { backgroundColor: "transparent" },
              ]}
              onPress={() => switchToTab(tab.key)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.tabBtnText,
                  isActive
                    ? styles.tabBtnTextActive
                    : { color: colors.textMuted },
                ]}
              >
                {tab.label}
              </Text>
              {tab.showBadge && testCount > 0 && (
                <View
                  style={[
                    styles.badge,
                    isActive
                      ? styles.badgeActive
                      : { backgroundColor: colors.border },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      isActive
                        ? styles.badgeTextActive
                        : { color: colors.textMuted },
                    ]}
                  >
                    {testCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* AIT-251: subtitle for the active tab (helps disambiguate Test vs Quiz) */}
      {(() => {
        const active = TABS.find((tab) => tab.key === activeTab);
        if (!active?.subtitle) return null;
        return (
          <Text style={[styles.tabSubtitle, { color: colors.textSecondary }]}>
            {active.subtitle}
          </Text>
        );
      })()}

      {/* Tab content */}
      {renderTabContent()}
    </ScrollView>
  );
}

/* Expose tab keys for external use */
TeacherTestsPage.TAB_KEYS = TAB_KEYS;

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      bg: "#0f172a",
      cardBg: "#1e293b",
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      accent: "#818cf8",
      accentHover: "#6366f1",
      accentLight: "rgba(99,102,241,0.12)",
    };
  }
  return {
    bg: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    accent: "#6366f1",
    accentHover: "#4f46e5",
    accentLight: "#eef2ff",
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    maxWidth: 1400,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 48,
  },
  /* Tab nav - pill container */
  tabNav: {
    flexDirection: "row",
    gap: 4,
    borderRadius: 10,
    padding: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
    marginBottom: 28,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2,
    elevation: 1,
  },
  tabBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 6,
  },
  tabBtnActive: {
    shadowColor: "rgba(99,102,241,0.3)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  tabBtnText: {
    fontSize: 14,
    fontWeight: "600",
  },
  tabBtnTextActive: {
    color: "#ffffff",
  },
  /* Badge */
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    paddingHorizontal: 6,
  },
  badgeActive: {
    backgroundColor: "rgba(255,255,255,0.25)",
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "700",
  },
  badgeTextActive: {
    color: "#ffffff",
  },
  /* Placeholder content */
  placeholder: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 80,
  },
  placeholderText: {
    fontSize: 16,
    fontWeight: "600",
  },
  /* AIT-251: subtitle under the active tab */
  tabSubtitle: {
    fontSize: 13,
    fontStyle: "italic",
    marginTop: -16,
    marginBottom: 20,
  },
});
